/**
 * Small, locally-styled input primitives shared across every layout form.
 *
 * The deck editor lives next to other features that use Radix Themes,
 * but we deliberately keep these inputs un-themed for now so the editor
 * stays self-contained and readable in the inspector pane regardless of
 * what the surrounding ThemeProvider declares.
 */

import type { ChangeEvent } from "react";

interface TextFieldProps {
  label: string;
  value: string;
  onChange(value: string): void;
  onBlur?(): void;
  placeholder?: string;
  /** Use a monospace font — helpful for class/slug fields. */
  mono?: boolean;
  /** Data-testid on the input itself. */
  testId?: string;
}

export function TextField({
  label,
  value,
  onChange,
  onBlur,
  placeholder,
  mono = false,
  testId,
}: TextFieldProps) {
  return (
    <label style={labelStyle}>
      <span style={labelTextStyle}>{label}</span>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onChange(e.target.value)
        }
        onBlur={onBlur}
        style={{
          ...inputStyle,
          fontFamily: mono ? "monospace" : "inherit",
        }}
        data-testid={testId}
      />
    </label>
  );
}

interface TextAreaProps {
  label: string;
  value: string;
  onChange(value: string): void;
  onBlur?(): void;
  rows?: number;
  /** Treat the content as code (HTML / JSON) — bumps to monospace. */
  mono?: boolean;
  testId?: string;
}

export function TextArea({
  label,
  value,
  onChange,
  onBlur,
  rows = 6,
  mono = true,
  testId,
}: TextAreaProps) {
  return (
    <label style={labelStyle}>
      <span style={labelTextStyle}>{label}</span>
      <textarea
        value={value}
        rows={rows}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
          onChange(e.target.value)
        }
        onBlur={onBlur}
        style={{
          ...inputStyle,
          fontFamily: mono ? "monospace" : "inherit",
          resize: "vertical",
        }}
        spellCheck={false}
        data-testid={testId}
      />
    </label>
  );
}

interface CheckboxProps {
  label: string;
  checked: boolean;
  onChange(checked: boolean): void;
  onBlur?(): void;
  testId?: string;
}

export function Checkbox({
  label,
  checked,
  onChange,
  onBlur,
  testId,
}: CheckboxProps) {
  return (
    <label
      style={{ ...labelStyle, flexDirection: "row", alignItems: "center" }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        onBlur={onBlur}
        style={{ marginRight: "0.5rem" }}
        data-testid={testId}
      />
      <span style={labelTextStyle}>{label}</span>
    </label>
  );
}

// ── Shared styles ──────────────────────────────────────────────────────────

const labelStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  marginBottom: "0.75rem",
  fontSize: "0.9em",
};

const labelTextStyle: React.CSSProperties = {
  color: "var(--gray-11)",
  fontSize: "0.8em",
  marginBottom: "0.25rem",
};

const inputStyle: React.CSSProperties = {
  padding: "0.45rem 0.55rem",
  border: "1px solid var(--gray-7)",
  borderRadius: 4,
  background: "var(--gray-1)",
  color: "var(--gray-12)",
  fontSize: "0.9em",
  width: "100%",
  boxSizing: "border-box",
};
